import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { CompanyProfileSectionTitle } from "./CompanyProfileSectionTitle";
import { Column, ErrorText, LoadingIndicator, Row } from "@/components";
import { CompanyLogosSection } from "@/components/companies/profile";
import { FormArrayInput } from "@/components/form/FormArrayInput";
import { FormEnumSelect } from "@/components/form/FormEnumSelect";
import { FormNumberInput } from "@/components/form/FormNumberInput";
import { FormRichTextInput } from "@/components/form/FormRichTextInput";
import { FormTextInput } from "@/components/form/FormTextInput";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";

import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useCreateCompanyInformation } from "@/hooks/mutations/useCreateCompanyInformation";
import { useUpdateCompanyInformation } from "@/hooks/mutations/useUpdateCompanyInformation";
import { useSetCompanyInformation } from "@/hooks/sets/useSetCompanyInformation";
import { paymentTermsOptions } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { nullableNumberSchema } from "@/lib/schemas";
import {
  CompanyInformation,
  CompanySummary,
  CreateOrUpdateCompanyInformationRequest,
  PaymentTerms,
} from "@/types";

const companyInformationSchema = z.object({
  companyAbbreviation: z
    .string()
    .length(3, "Must be exactly 3 letters")
    .regex(/^[A-Z]{3}$/, "Must be 3 uppercase letters"),
  address: z.string(),
  description: z.string(),
  naicsCodes: z.array(z.string().max(6)),
  customerIndustries: z.array(z.string()),
  manufacturingCapabilities: z.array(z.string()),
  equipment: z.array(z.string()),
  materials: z.array(z.string()),
  headcount: nullableNumberSchema,
  foundedYear: z.string(),
  location: z.string(),
  dunsNumber: z.string(),
  cageCode: z.string(),
  paymentTerms: z.nativeEnum(PaymentTerms, {
    required_error: "Payment terms are required",
  }),
  websiteUrl: z.union([z.string().url(), z.literal("")]),
});

export const CompanyProfileInformationForm = ({
  company,
  companyInformation,
  logoSignedUrl,
  setIsEditing,
}: {
  company: CompanySummary;
  companyInformation: CompanyInformation | null;
  logoSignedUrl: string | null;
  setIsEditing: (isEditing: boolean) => void;
}) => {
  const {
    mutate: createCompanyInformation,
    isPending: isCreateCompanyInformationPending,
    isError: isCreateCompanyInformationError,
  } = useCreateCompanyInformation();
  const {
    mutate: updateCompanyInformation,
    isPending: isUpdateCompanyInformationPending,
    isError: isUpdateCompanyInformationError,
  } = useUpdateCompanyInformation();
  const setCompanyInformation = useSetCompanyInformation();

  const isMutationPending =
    isCreateCompanyInformationPending || isUpdateCompanyInformationPending;
  const isMutationError =
    isCreateCompanyInformationError || isUpdateCompanyInformationError;
  const successToast = useSuccessToast();

  const form = useForm({
    defaultValues: {
      companyAbbreviation:
        companyInformation?.companyInformation.companyAbbreviation ??
        company.name
          .replace(/[^a-zA-Z]/g, "")
          .slice(0, 3)
          .toUpperCase()
          .padEnd(3, "X"),
      address: companyInformation?.companyInformation.address ?? "",
      description: companyInformation?.companyInformation.description ?? "",
      naicsCodes: companyInformation?.companyInformation.naicsCodes ?? [],
      customerIndustries:
        companyInformation?.companyInformation.customerIndustries ?? [],
      manufacturingCapabilities:
        companyInformation?.companyInformation.manufacturingCapabilities ?? [],
      equipment: companyInformation?.companyInformation.equipment ?? [],
      materials: companyInformation?.companyInformation.materials ?? [],
      headcount: companyInformation?.companyInformation.headcount ?? null,
      foundedYear: companyInformation?.companyInformation.foundedYear ?? "",
      location: companyInformation?.companyInformation.location ?? "",
      dunsNumber: companyInformation?.companyInformation.dunsNumber ?? "",
      cageCode: companyInformation?.companyInformation.cageCode ?? "",
      paymentTerms:
        companyInformation?.companyInformation.paymentTerms ??
        PaymentTerms.NET60,
      websiteUrl: companyInformation?.companyInformation.websiteUrl ?? "",
    },
    resolver: zodResolver(companyInformationSchema),
  });

  const handleSubmit = (data: z.infer<typeof companyInformationSchema>) => {
    mixpanelUtils.trackButtonClick("Save Changes", MIXPANEL_SCREENS.COMPANY);
    const dirtyFields = form.formState.dirtyFields;
    mixpanelUtils.trackFormSubmission("Profile save", MIXPANEL_SCREENS.COMPANY, {
      fields_modified: Object.keys(dirtyFields).filter((k) => dirtyFields[k as keyof typeof dirtyFields]),
      has_description: !!data.description,
      has_address: !!data.address,
      has_websiteUrl: !!data.websiteUrl,
      has_headcount: data.headcount != null,
    });
    const createOrUpdateCompanyInformationRequest: CreateOrUpdateCompanyInformationRequest =
      {
        companyInformation: {
          ...data,
          companyAbbreviation: data.companyAbbreviation.toUpperCase(),
          logoCompanyFileId:
            companyInformation?.companyInformation.logoCompanyFileId ?? null,
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          smallLogoCompanyFileId: companyInformation?.companyInformation.smallLogoCompanyFileId || null,
          // TODO: Find a more robust way to handle nullable fields.
          address: data.address || null,
          description: data.description || null,
          headcount: data.headcount ?? null,
          foundedYear: data.foundedYear || null,
          location: data.location || null,
          dunsNumber: data.dunsNumber || null,
          cageCode: data.cageCode || null,
          websiteUrl: data.websiteUrl || null,
        },
      };

    if (companyInformation) {
      updateCompanyInformation(
        {
          companyInformationId: companyInformation.id,
          updateCompanyInformationRequest:
            createOrUpdateCompanyInformationRequest,
        },
        {
          onSuccess: (data) => {
            handleSuccess(data);
          },
        },
      );
    } else {
      createCompanyInformation(
        {
          createCompanyInformationRequest:
            createOrUpdateCompanyInformationRequest,
        },
        {
          onSuccess: (data) => {
            handleSuccess(data);
          },
        },
      );
    }
  };

  const handleSuccess = (companyInformation: CompanyInformation) => {
    setIsEditing(false);
    setCompanyInformation({
      companyInformation,
    });
    successToast({
      description: "Company information updated successfully!",
    });
  };

  const render = () => {
    return (
      <Form {...form}>
        <form
          className="flex flex-col gap-8"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          {/* Header Section */}
          <Row className="items-center gap-4">
            <Column className="w-full gap-1">
              <Row className="items-center gap-4">
                <p className="text-2xl font-semibold text-slate-700">
                  {company.name}
                </p>
                <FormTextInput
                  fieldName="companyAbbreviation"
                  form={form}
                  label="Abbreviation"
                  className="w-24"
                  onBlur={(e) => {
                    const value = e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z]/g, "")
                      .slice(0, 3);
                    form.setValue("companyAbbreviation", value);
                  }}
                />
              </Row>
              <FormTextInput
                fieldName="websiteUrl"
                form={form}
                placeholder="Website URL"
              />
              <FormTextInput
                fieldName="address"
                form={form}
                placeholder="Address"
              />
            </Column>
          </Row>
          <CompanyLogosSection
            companyName={company.name}
            companyInformation={companyInformation}
            logoSignedUrl={logoSignedUrl}
          />
          {/* About Section */}
          <FormRichTextInput
            fieldName="description"
            form={form}
            label="About"
          />
          {/* NAICS Codes Section */}
          <Column className="gap-2">
            <CompanyProfileSectionTitle title="NAICS Code(s)" />
            <FormArrayInput
              fieldName="naicsCodes"
              form={form}
              inputType="number"
              placeholder="NAICS Code(s)"
            />
          </Column>
          {/* Customer Industries Section */}
          <Column className="gap-2">
            <CompanyProfileSectionTitle title="Industries of Our Customers" />
            <FormArrayInput
              fieldName="customerIndustries"
              form={form}
              placeholder="Industries"
            />
          </Column>

          {/* Manufacturing Capabilities Section */}
          <Column className="gap-2">
            <CompanyProfileSectionTitle title="Manufacturing Capabilities / Products" />
            <FormArrayInput
              fieldName="manufacturingCapabilities"
              form={form}
              placeholder="Capabilities"
            />
          </Column>
          {/* Equipment Section */}
          <Column className="gap-2">
            <CompanyProfileSectionTitle title="Equipment" />
            <FormArrayInput
              fieldName="equipment"
              form={form}
              placeholder="Equipment"
            />
          </Column>
          {/* Materials Section */}
          <Column className="gap-2">
            <CompanyProfileSectionTitle title="Materials" />
            <FormArrayInput
              fieldName="materials"
              form={form}
              placeholder="Materials"
            />
          </Column>
          {/* Company Details Section */}
          <Column className="gap-2">
            <CompanyProfileSectionTitle title="Company Details" />
            <div className="grid grid-cols-3 gap-4">
              {/* Headcount */}
              <FormNumberInput
                fieldName="headcount"
                form={form}
                label="Headcount"
                placeholder="Headcount"
              />
              {/* Founded In */}
              <FormTextInput
                fieldName="foundedYear"
                form={form}
                label="Founded In"
                placeholder="Founded In"
              />
              {/* Location */}
              <FormTextInput
                fieldName="location"
                form={form}
                label="Location"
                placeholder="Ex: Knoxville, TN"
              />
              {/* D-U-N-S Number */}
              <FormTextInput
                fieldName="dunsNumber"
                form={form}
                label="D-U-N-S Number"
                placeholder="D-U-N-S Number"
              />
              {/* CAGE Code */}
              <FormTextInput
                fieldName="cageCode"
                form={form}
                label="CAGE Code"
                placeholder="CAGE Code"
              />
              {/* Payment Terms */}
              <FormEnumSelect
                fieldName="paymentTerms"
                form={form}
                label="Payment Terms"
                options={paymentTermsOptions}
              />
            </div>
          </Column>
          <Row className="justify-end gap-2">
            <Button
              disabled={isMutationPending}
              onClick={() => {
                mixpanelUtils.trackButtonClick(
                  "Cancel profile edit",
                  MIXPANEL_SCREENS.COMPANY,
                );
                setIsEditing(false);
              }}
              type="button"
              variant="ghost"
            >
              Cancel
            </Button>
            <Button
              className="relative"
              disabled={isMutationPending}
              type="submit"
            >
              <span className={isMutationPending ? "invisible" : ""}>
                Save Changes
              </span>
              {isMutationPending && (
                <LoadingIndicator
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                  gearColor="white"
                  variant="small"
                />
              )}
            </Button>
          </Row>
          {isMutationError && (
            <Row className="justify-end">
              <ErrorText errorMessage="An error occurred while saving. Please try again." />
            </Row>
          )}
        </form>
      </Form>
    );
  };

  return render();
};
